import { useState, useEffect } from 'react';
import { User, Key, Save, Trash2, Eye, EyeOff, Link2, Plus, X, StickyNote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

export const CredentialDialog = ({ 
  isOpen, 
  onClose, 
  platform, 
  currentCredentials, 
  onSave, 
  onDelete 
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [links, setLinks] = useState([]);
  const [notes, setNotes] = useState('');
  const [newLinkName, setNewLinkName] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');

  const platformName = platform?.replace('www.', '').replace('.com', '').replace('.us', '') || '';

  useEffect(() => {
    if (isOpen && currentCredentials) {
      setUsername(currentCredentials.username || '');
      setPassword(currentCredentials.password || '');
      setLinks(currentCredentials.links || []);
      setNotes(currentCredentials.notes || '');
    }
  }, [isOpen, currentCredentials]);

  const handleAddLink = () => {
    if (!newLinkUrl.trim()) {
      toast.error('Ingresa una URL para el link');
      return;
    }
    
    let url = newLinkUrl.trim();
    // Add https:// if no protocol
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    const newLink = {
      id: Date.now(),
      name: newLinkName.trim() || `Link ${links.length + 1}`,
      url: url
    };
    
    setLinks([...links, newLink]);
    setNewLinkName('');
    setNewLinkUrl('');
    toast.success('Link agregado');
  };

  const handleRemoveLink = (linkId) => {
    setLinks(links.filter(l => l.id !== linkId));
  };

  const handleSave = () => {
    if (!username.trim() && !password.trim() && links.length === 0 && !notes.trim()) {
      toast.error('Ingresa al menos un dato');
      return;
    }
    onSave(platform, username.trim(), password.trim(), links, notes.trim());
    toast.success('Datos guardados correctamente', {
      icon: <Save className="w-4 h-4 text-[#80C753]" />
    });
    onClose();
  };

  const handleDelete = () => {
    onDelete(platform);
    toast.success('Datos eliminados', {
      icon: <Trash2 className="w-4 h-4 text-red-500" />
    });
    onClose();
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    setShowPassword(false);
    setLinks([]);
    setNotes('');
    setNewLinkName('');
    setNewLinkUrl('');
    onClose();
  };

  const hasAnyData = currentCredentials?.username || currentCredentials?.password || (currentCredentials?.links && currentCredentials.links.length > 0) || currentCredentials?.notes;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        data-testid={`credential-dialog-${platformName}`}
        className="sm:max-w-lg bg-white dark:bg-[#151B2B] border-slate-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-[#1D1D1B] dark:text-white flex items-center gap-2">
            <Key className="h-5 w-5 text-[#45A0EE]" />
            Datos de Acceso
          </DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-400">
            {platform}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Credentials Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <User className="h-4 w-4" />
              Credenciales (opcional)
            </h3>
            
            {/* Username Field */}
            <div className="space-y-2">
              <Label 
                htmlFor="username" 
                className="text-sm font-medium text-[#1D1D1B] dark:text-white"
              >
                Usuario / Email
              </Label>
              <Input
                data-testid={`input-username-${platformName}`}
                id="username"
                type="text"
                placeholder="Ingresa tu usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 focus-visible:ring-[#45A0EE]"
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label 
                htmlFor="password" 
                className="text-sm font-medium text-[#1D1D1B] dark:text-white"
              >
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  data-testid={`input-password-${platformName}`}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Ingresa tu contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 focus-visible:ring-[#45A0EE] pr-10"
                />
                <Button
                  data-testid={`toggle-password-visibility-${platformName}`}
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-200 dark:border-slate-700" />

          {/* Links Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Links de Afiliado (opcional)
            </h3>

            {/* Existing Links */}
            {links.length > 0 && (
              <div className="space-y-2">
                {links.map((link) => (
                  <div 
                    key={link.id}
                    data-testid={`link-item-${link.id}`}
                    className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2"
                  >
                    <Link2 className="h-4 w-4 text-[#45A0EE] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1D1D1B] dark:text-white truncate">
                        {link.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {link.url}
                      </p>
                    </div>
                    <Button
                      data-testid={`remove-link-${link.id}`}
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveLink(link.id)}
                      className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Link */}
            <div className="space-y-2 p-3 bg-slate-100/50 dark:bg-slate-800/50 rounded-lg">
              <Label className="text-xs text-slate-500 dark:text-slate-400">
                Agregar nuevo link
              </Label>
              <div className="flex gap-2">
                <Input
                  data-testid={`input-link-name-${platformName}`}
                  type="text"
                  placeholder="Nombre (ej: Mi link)"
                  value={newLinkName}
                  onChange={(e) => setNewLinkName(e.target.value)}
                  className="flex-1 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-sm h-9"
                />
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid={`input-link-url-${platformName}`}
                  type="text"
                  placeholder="URL del link"
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddLink()}
                  className="flex-1 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-sm h-9"
                />
                <Button
                  data-testid={`add-link-btn-${platformName}`}
                  type="button"
                  onClick={handleAddLink}
                  className="bg-[#45A0EE] hover:bg-[#3590de] text-white h-9 px-3"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-200 dark:border-slate-700" />

          {/* Notes Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <StickyNote className="h-4 w-4 text-amber-500" />
              Notas Personales (opcional)
            </h3>
            
            <Textarea
              data-testid={`input-notes-${platformName}`}
              placeholder="Ej: Contacto: Juan - 555-1234 | Usar código PROMO2025 | Renovar en diciembre..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700/30 focus-visible:ring-amber-400 min-h-[80px] text-sm"
            />
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Usa este espacio para guardar información importante: contactos, códigos promocionales, fechas de renovación, etc.
            </p>
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          {hasAnyData && (
            <Button
              data-testid={`delete-credentials-${platformName}`}
              variant="outline"
              onClick={handleDelete}
              className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          )}
          <Button
            data-testid={`cancel-dialog-${platformName}`}
            variant="outline"
            onClick={handleClose}
            className="border-slate-200 dark:border-slate-600"
          >
            Cancelar
          </Button>
          <Button
            data-testid={`save-credentials-${platformName}`}
            onClick={handleSave}
            className="bg-[#80C753] hover:bg-[#6ab043] text-white"
          >
            <Save className="h-4 w-4 mr-2" />
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CredentialDialog;
